import React, { useState, useEffect } from 'react';
import {
  CardBody,
  Row,
  Col,
  Card,
  Container,
  CardHeader,
  Modal,
  ModalFooter,
  ModalHeader,
  ModalBody,
  Label,
  Input,
  Button,
} from 'reactstrap';
import DataTable from 'react-data-table-component';
import BreadCrumb from '../../../Components/Common/BreadCrumb';
import DeleteModal from '../../../Components/Common/DeleteModal';
import Can from '../../../Components/Common/Can';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { proHydroApi } from '../../../helpers/crude_api_helper';

const emptyFungsiForm = {
  id: null,
  companyId: '',
  fungsiCode: '',
  fungsiName: '',
};

const FungsiView = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [companies, setCompanies] = useState([]);

  const [modalFungsi, setModalFungsi] = useState(false);
  const [fungsiFormData, setFungsiFormData] = useState(emptyFungsiForm);
  const [errors, setErrors] = useState({});

  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  const [totalRows, setTotalRows] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState(null);

  document.title = 'Data Fungsi';

  useEffect(() => {
    fetchCompanies();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
    }, 500);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, rowsPerPage, debouncedSearch, sortField, sortDirection]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, sortField, sortDirection]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = { page: currentPage, pageSize: rowsPerPage };
      if (debouncedSearch) params.searchText = debouncedSearch;
      if (sortField) {
        params.sortField = sortField;
        params.sortOrder = sortDirection === 'asc' ? 'Asc' : 'Desc';
      }
      const result = await proHydroApi.get('/v1/Fungsis', params);
      const list = Array.isArray(result?.items) ? result.items : [];
      setItems(list);
      setTotalRows(result?.totalCount || 0);
    } catch (error) {
      toast.error(`Failed to load data: ${error}`);
      setItems([]);
      setTotalRows(0);
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanies = async () => {
    try {
      const result = await proHydroApi.get('/v1/Companies/Lookup');
      const list = Array.isArray(result?.items) ? result.items : [];
      setCompanies(list);
    } catch (error) {
      toast.error(`Failed to load companies: ${error}`);
      setCompanies([]);
    }
  };

  const handleChange = e => {
    const { name, value } = e.target;
    setFungsiFormData({ ...fungsiFormData, [name]: value });
    setErrors({ ...errors, [name]: '' });
  };

  const validateForm = () => {
    const newErrors = {};
    if (!fungsiFormData.fungsiCode) {
      newErrors.fungsiCode = 'Fungsi ID is required';
    } else if (fungsiFormData.fungsiCode.length > 5) {
      newErrors.fungsiCode = 'Fungsi ID max 5 characters';
    }
    if (!fungsiFormData.fungsiName) {
      newErrors.fungsiName = 'Fungsi Name is required';
    } else if (fungsiFormData.fungsiName.length > 100) {
      newErrors.fungsiName = 'Fungsi Name max 100 characters';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNewModal = () => {
    setFungsiFormData(emptyFungsiForm);
    setErrors({});
    setModalFungsi(true);
  };

  const handleUpdateModal = row => {
    setFungsiFormData({
      id: row.id,
      companyId: row.companyId || '',
      fungsiCode: row.fungsiCode || '',
      fungsiName: row.fungsiName || '',
    });
    setErrors({});
    setModalFungsi(true);
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    if (fungsiFormData.id) {
      try {
        await proHydroApi.put(`/v1/Fungsis/${fungsiFormData.id}`, {
          id: fungsiFormData.id,
          companyId: fungsiFormData.companyId || null,
          fungsiCode: fungsiFormData.fungsiCode,
          fungsiName: fungsiFormData.fungsiName,
          fungsis: [],
        });
        toast.success('Fungsi has been updated');
      } catch (error) {
        toast.error(`Failed to save data: ${error}`);
      } finally {
        setModalFungsi(false);
        fetchData();
      }
    } else {
      try {
        await proHydroApi.create('/v1/Fungsis', {
          companyId: fungsiFormData.companyId || null,
          fungsiCode: fungsiFormData.fungsiCode,
          fungsiName: fungsiFormData.fungsiName,
        });
        toast.success('Fungsi has been added');
      } catch (error) {
        toast.error(`Failed to save data: ${error}`);
      } finally {
        setModalFungsi(false);
        fetchData();
      }
    }
  };

  const handleDeleteModal = row => {
    setSelectedRow(row);
    setDeleteModal(true);
  };

  const handleDelete = async () => {
    if (!selectedRow) return;
    try {
      await proHydroApi.delete(`/v1/Fungsis/${selectedRow.id}`);
      toast.success('Fungsi has been deleted');
    } catch (error) {
      toast.error(`Failed to delete data: ${error}`);
    } finally {
      setDeleteModal(false);
      setSelectedRow(null);
      fetchData();
    }
  };

  const moveRow = async (row, direction) => {
    const index = items.findIndex(item => item.id === row.id);
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    if (swapIndex < 0 || swapIndex >= items.length) return;

    const current = items[index];
    const swapWith = items[swapIndex];

    try {
      await proHydroApi.put('/v1/Fungsis/sort-order', {
        fungsis: [
          { id: current.id, sortOrder: swapWith.sortOrder },
          { id: swapWith.id, sortOrder: current.sortOrder },
        ],
      });
      fetchData();
    } catch (error) {
      toast.error(`Failed to reorder data: ${error}`);
    }
  };

  const headerLabel = label => (
    <span className='form-label text-uppercase fw-semibold fs-13 mb-0'>
      {label}
    </span>
  );

  const handleSort = (column, direction) => {
    setSortField(column.sortField || null);
    setSortDirection(direction);
  };

  const columns = [
    {
      id: 'companiesCompanyName',
      name: headerLabel('Company'),
      selector: row =>
        `${row.companiesCompanyCode || ''} - ${row.companiesCompanyName || ''}`,
      sortable: true,
      sortField: 'CompanyId',
      wrap: true,
    },
    {
      id: 'fungsiCode',
      name: headerLabel('Fungsi ID'),
      selector: row => row.fungsiCode,
      sortable: true,
      sortField: 'FungsiCode',
      wrap: true,
    },
    {
      id: 'fungsiName',
      name: headerLabel('Fungsi Name'),
      selector: row => row.fungsiName,
      sortable: true,
      sortField: 'FungsiName',
      wrap: true,
    },
    {
      name: (
        <span className='form-label text-uppercase fw-semibold fs-13'>
          Order
        </span>
      ),
      width: '110px',
      center: true,
      cell: row => {
        const index = items.findIndex(item => item.id === row.id);
        return (
          <div className='d-flex gap-1'>
            <button
              type='button'
              className='btn btn-soft-secondary btn-sm btn-icon'
              disabled={index <= 0}
              onClick={() => moveRow(row, 'up')}
            >
              <i className='ri-arrow-up-line align-bottom'></i>
            </button>
            <button
              type='button'
              className='btn btn-soft-secondary btn-sm btn-icon'
              disabled={index === items.length - 1}
              onClick={() => moveRow(row, 'down')}
            >
              <i className='ri-arrow-down-line align-bottom'></i>
            </button>
          </div>
        );
      },
    },
    {
      name: (
        <span className='form-label text-uppercase fw-semibold fs-13'>
          Action
        </span>
      ),
      width: '110px',
      center: true,
      cell: row => (
        <div className='d-flex gap-1 justify-content-end'>
          <Can action='canUpdate'>
            <button
              className='btn btn-soft-info btn-sm btn-icon'
              onClick={() => handleUpdateModal(row)}
            >
              <i className='ri-pencil-fill align-bottom'></i>
            </button>
          </Can>
          <Can action='canDelete'>
            <button
              className='btn btn-soft-danger btn-sm btn-icon'
              onClick={() => handleDeleteModal(row)}
            >
              <i className='ri-delete-bin-6-line align-bottom'></i>
            </button>
          </Can>
        </div>
      ),
    },
  ];

  return (
    <React.Fragment>
      <div className='page-content'>
        <DeleteModal
          show={deleteModal}
          onDeleteClick={handleDelete}
          onCloseClick={() => setDeleteModal(false)}
        />
        <Container fluid>
          <BreadCrumb title='Fungsi' pageTitle='CRUDE' />

          <Row>
            <Col lg={12}>
              <Card id='FungsiList'>
                <CardHeader className='border-0'>
                  <div className='d-flex align-items-center flex-wrap gap-2'>
                    <h5 className='card-title mb-0 flex-grow-1'>Data Fungsi</h5>
                    <div className='flex-shrink-0'>
                      <div className='d-flex gap-2 flex-wrap'>
                        <div className='search-box'>
                          <Input
                            type='text'
                            className='form-control form-control-sm'
                            placeholder='Search...'
                            value={searchInput}
                            onChange={e => setSearchInput(e.target.value)}
                          />
                        </div>
                        <Can action='canCreate'>
                          <button
                            type='button'
                            className='btn btn-primary btn-label right ms-auto nexttab btn-sm'
                            onClick={handleNewModal}
                          >
                            <i className='ri-play-list-add-line label-icon align-middle fs-16 ms-2'></i>{' '}
                            <span className='d-none d-sm-inline'>
                              Create New Fungsi
                            </span>
                            <span className='d-sm-none'>New</span>
                          </button>
                        </Can>
                      </div>
                    </div>
                  </div>
                </CardHeader>

                <CardBody className='pt-0'>
                  <div
                    className='table-responsive'
                    style={{
                      minHeight: 'auto',
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <DataTable
                      columns={columns}
                      highlightOnHover
                      data={items}
                      pagination
                      paginationServer
                      paginationTotalRows={totalRows}
                      paginationPerPage={rowsPerPage}
                      paginationRowsPerPageOptions={[15, 25, 50, 100]}
                      onChangePage={page => setCurrentPage(page)}
                      onChangeRowsPerPage={newPerPage => {
                        setRowsPerPage(newPerPage);
                        setCurrentPage(1);
                      }}
                      sortServer
                      onSort={handleSort}
                      wrap
                      dense
                      striped
                      progressPending={loading}
                      fixedHeader={false}
                      noDataComponent={
                        <div
                          style={{
                            padding: '20px',
                            textAlign: 'center',
                            color: '#6c757d',
                          }}
                        >
                          <i
                            className='ri-database-2-line'
                            style={{
                              fontSize: '32px',
                              marginBottom: '8px',
                              display: 'block',
                            }}
                          ></i>
                          No fungsi found
                        </div>
                      }
                      customStyles={{
                        header: {
                          style: {
                            backgroundColor: '#f0f0f0',
                            color: '#333',
                            fontWeight: 'bold',
                          },
                        },
                        headCells: {
                          style: {
                            backgroundColor: '#f0f0f0',
                            fontWeight: 'bold',
                            fontSize: '14px',
                            paddingLeft: '12px',
                            paddingRight: '12px',
                            borderRight: '1px solid #dee2e6',
                            position: 'relative',
                            overflow: 'visible',
                          },
                        },
                        cells: {
                          style: {
                            paddingLeft: '12px',
                            paddingRight: '12px',
                            wordBreak: 'break-word',
                            borderRight: '1px solid #dee2e6',
                          },
                        },
                        rows: {
                          style: {
                            minHeight: '50px',
                            '&:hover': {
                              backgroundColor: '#f8f9fa',
                            },
                          },
                        },
                        table: {
                          style: {
                            width: '100%',
                            minWidth: '100%',
                          },
                        },
                      }}
                    />

                    <ToastContainer closeButton={false} limit={1} />
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          <Modal
            isOpen={modalFungsi}
            role='dialog'
            autoFocus={true}
            centered
            id='addFungsiModal'
            toggle={() => setModalFungsi(!modalFungsi)}
            data-bs-backdrop='static'
            size='md'
          >
            <ModalHeader
              className='bg-soft-info'
              toggle={() => setModalFungsi(!modalFungsi)}
            >
              <span className='modal-title text-info fw-bold'>
                {fungsiFormData.id !== null ? 'Edit Fungsi' : 'Add New Record'}
              </span>
            </ModalHeader>

            <ModalBody>
              <div className='d-flex mt-2'>
                <Col sm={12}>
                  <Row>
                    <Col sm={12}>
                      <div className='mb-3'>
                        <Label for='companyId' className='form-label'>
                          Company Code
                        </Label>
                        <Input
                          type='select'
                          id='companyId'
                          name='companyId'
                          className='form-control'
                          value={fungsiFormData.companyId || ''}
                          onChange={handleChange}
                        >
                          <option value=''>Select Company</option>
                          {companies.map(company => (
                            <option key={company.id} value={company.id}>
                              {company.text}
                            </option>
                          ))}
                        </Input>
                      </div>
                    </Col>
                    <Col sm={12}>
                      <div className='mb-3'>
                        <Label for='fungsiCode' className='form-label'>
                          Fungsi ID
                        </Label>
                        <Input
                          type='text'
                          id='fungsiCode'
                          name='fungsiCode'
                          className='form-control'
                          maxLength={5}
                          invalid={!!errors.fungsiCode}
                          value={fungsiFormData.fungsiCode || ''}
                          onChange={handleChange}
                        />
                        {errors.fungsiCode && (
                          <div className='invalid-feedback d-block'>
                            {errors.fungsiCode}
                          </div>
                        )}
                      </div>
                    </Col>
                    <Col sm={12}>
                      <div className='mb-3'>
                        <Label for='fungsiName' className='form-label'>
                          Fungsi Name
                        </Label>
                        <Input
                          type='text'
                          id='fungsiName'
                          name='fungsiName'
                          className='form-control'
                          maxLength={100}
                          invalid={!!errors.fungsiName}
                          value={fungsiFormData.fungsiName || ''}
                          onChange={handleChange}
                        />
                        {errors.fungsiName && (
                          <div className='invalid-feedback d-block'>
                            {errors.fungsiName}
                          </div>
                        )}
                      </div>
                    </Col>
                  </Row>
                </Col>
              </div>
            </ModalBody>

            <ModalFooter>
              <Button
                color='light'
                className='btn-sm'
                onClick={() => setModalFungsi(false)}
              >
                Cancel
              </Button>
              <Button color='primary' className='btn-sm' onClick={handleSave}>
                Save
              </Button>
            </ModalFooter>
          </Modal>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default FungsiView;
